import React from 'react'
import './notification-test-details.scss'

import deepEqual from 'react-fast-compare'
import { observe } from 'mobx'
import { observer } from 'mobx-react'
import { Container } from 'typescript-ioc/es5'
import * as moment from 'moment-timezone'
import * as randomstring from 'randomstring'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import autobind from 'autobind-decorator'
import {
    AndroidFilled,
    AppleFilled,
    ChromeFilled,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    ReloadOutlined,
} from '@ant-design/icons'
import { FormComponentProps } from '@ant-design/compatible/lib/form'
import {
    BASE_TIME_FORMAT,
    BASE_TIME_FORMAT_WITHOUT_TZ,
    DEFAULT_HIGHCHARTS_CONFIG,
    FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR,
    INSIGHTS_ANDROID_COLOR,
    INSIGHTS_IOS_COLOR,
    INSIGHTS_WEB_COLOR,
} from '../../../constants'
import { arrayContains, preventBubbling, titleCase } from '../../../_utils/utils'
import { DomainDto } from '../../../dtos/domain'
import { StatusType } from '../../../enums/status-type'
import { AppService } from '../../../services/app'
import { NotificationService } from '../../../services/notification'
import { HighchartsUtils } from '../../../_utils/highcharts-utils'
import { AppState } from '../../../stores/app'
import {
    GetDomainNotificationABTestResponse,
    GetNotificationResponse,
    GetNotificationScheduleResponse,
} from '@pushly/models/lib/structs/notifications'
import { GetSegmentResponse } from '@pushly/models/lib/structs/segmentation'
import { isTzDelivery } from '../helpers/notification-helpers'
import { NOTIFICATION_FIELD_DESCRIPTIONS } from '../field-descriptions'
import { NotificationSummaryCard, StatusBadge, PushlyAi, Well } from '@pushly/aqe/lib/components'
import {
    DetailsSummaryWell,
    DetailsSummarySection,
    DetailsSummarySubSections,
    DetailsSummaryField,
} from '@pushly/aqe/lib/components/details-summary-well'
import { BaseNotificationDetails, IBaseNotificationDetailsState } from './base-notification-details-component'
import { InsightsService } from '../../../services/insights'
import * as clone from 'clone'
import { NotificationAbWinnerBadge } from '../../../components/badges/notification-ab-winner-badge'
import { INotificationTreatment } from '../../../interfaces/notification-treatment'
import { extractAiFields, extractSummaryNotification } from '../../../_utils/notifications'
import { NoTranslate } from '../../../components/no-translate/no-translate'
import { NotificationStatusBadge } from '../../../components/badges/notification-status-badge'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { getCollapseKeyDisplayLabel } from '@pushly/aqe/lib/utils/notification-collapse-key'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { NotificationPreviewState } from '../../../stores/notification-preview-state'
import { AbTestNotificationRole } from '@pushly/models/lib/enums/ab-test-notification-role'
import { ReportEntity } from '../../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../../reporting_v2/enums/report-date-increment'
import { PreviewPlatform } from '@pushly/models/lib/enums/preview-platform'
import { Button, Flex, Skeleton, Space, Tag, Tooltip, Typography } from 'antd'
import { DetailsViewPageHeader } from '../../../components/details-view/details-view-page-header'
import { StatsWell, StatItem } from '../../../components/details-view/stats-well/'
import { NotificationDeliveryWindow } from '@pushly/models/lib/enums'
import { NotificationDeliveryType } from '@pushly/models/lib/enums/notification-delivery-type'

Highcharts.setOptions({ lang: { thousandsSep: ',' } })

interface IHeaderProps {
    domain: DomainDto
    test?: GetDomainNotificationABTestResponse
}

interface IProps {}

interface IState extends IBaseNotificationDetailsState {
    domain: DomainDto
    test?: GetDomainNotificationABTestResponse
}

export class TestDetailsHeader extends React.Component<IHeaderProps, {}> {
    protected appState: AppState
    protected appService: AppService

    public constructor(props: IHeaderProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public render(): React.ReactNode {
        const testType = this.buildTestTypeValue()

        return (
            <div className="details-header">
                <div className="notif-title">
                    <h2>{this.test?.name}</h2>
                    <div className="append">
                        <div className="notif-id">
                            <span>
                                #{this.test?.id}
                                <span>
                                    {' '}
                                    | Created by{' '}
                                    <NoTranslate>{this.test?.created_by_user_name ?? 'Unknown'}</NoTranslate>
                                </span>
                            </span>
                        </div>
                    </div>
                </div>

                <DetailsSummaryWell loading={!this.test} collapsible={true}>
                    <DetailsSummarySection title="General">
                        <DetailsSummaryField label="ID" value={this.test?.id} copyable />
                        <DetailsSummaryField
                            label="Status"
                            info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.status, inline: true }}
                            value={
                                this.test && (
                                    <NotificationStatusBadge
                                        status={this.test.schedules[0]?.status}
                                        expanded={true}
                                        holdOut={
                                            !!this.test.notifications?.find(
                                                (n) => n.delivery_spec?.window === NotificationDeliveryWindow.HOLD_OUT,
                                            )
                                        }
                                    />
                                )
                            }
                        />
                        <DetailsSummaryField
                            label="Channels"
                            value={this.renderTestChannelValues()}
                            info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.channels, inline: true }}
                        />
                        {testType !== 'Content' && (
                            <DetailsSummaryField
                                label="Test Type"
                                value={testType}
                                info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.testType, inline: true }}
                            />
                        )}
                        {this.renderTotalVariantsField()}
                    </DetailsSummarySection>

                    <DetailsSummarySubSections>
                        <DetailsSummarySection title="Delivery">
                            <DetailsSummaryField
                                label="Delivery Date Range"
                                value={this.buildFlightDatesValue()}
                                info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.deliveryDateRange, inline: true }}
                            />
                            <DetailsSummaryField
                                label="Delivery Strategy"
                                value={this.buildTestStrategyValue()}
                                info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.deliveryStrategy, inline: true }}
                            />
                            {this.renderTestBehaviorsField()}
                        </DetailsSummarySection>

                        <DetailsSummarySection title="Targeting">{this.renderSegmentFields()}</DetailsSummarySection>
                    </DetailsSummarySubSections>

                    {this.renderTestContentSection()}
                </DetailsSummaryWell>
            </div>
        )
    }

    protected get test(): GetDomainNotificationABTestResponse | undefined {
        return this.props.test
    }

    protected get sample(): GetNotificationScheduleResponse {
        return this.test!.schedules[0]
    }

    protected buildTestTypeValue(): string {
        let testType = 'Content & Delivery'
        const test = this.test!
        const variants = test.schedules
        const deliverySpecs = variants.map((v) => v.delivery_spec)
        const allSpecsMatch = deliverySpecs.every((v, i, arr) => deepEqual(v, arr[0]))

        if (test.test_types?.length === 1) {
            testType = test.test_types[0] === 'content' ? 'Content' : 'Delivery'
        } else if (allSpecsMatch) {
            testType = 'Content'
        }

        return testType
    }

    protected buildFlightDatesValue(): React.ReactNode {
        const isSTZ = isTzDelivery(this.sample)
        const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT

        let startDeliverySpec
        let endDeliverySpec

        if (this.test && this.test.is_time_variant) {
            const sortedDeliverySpecs = this.test.schedules
                .map((s) => s.delivery_spec)
                .sort((a, b) => {
                    const aMoment = moment(a.send_date_utc!)
                    const bMoment = moment(b.send_date_utc!)

                    if (aMoment.isAfter(bMoment)) {
                        return 1
                    } else if (aMoment.isBefore(bMoment)) {
                        return -1
                    }

                    return 0
                })

            startDeliverySpec = sortedDeliverySpecs[0]
            endDeliverySpec = sortedDeliverySpecs[sortedDeliverySpecs.length - 1]
        } else {
            startDeliverySpec = endDeliverySpec = this.sample.delivery_spec
        }

        const startDate = moment.tz(startDeliverySpec.send_date_utc, startDeliverySpec.timezone)
        const expiryDate = moment
            .tz(endDeliverySpec.send_date_utc, endDeliverySpec.timezone)
            .add(endDeliverySpec.ttl_seconds, 'seconds')

        const flightDateStart = startDate.format(isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT)
        const flightDateEnd = expiryDate.format(sendDateFormat)

        return (
            <>
                {flightDateStart} <span style={{ opacity: 0.5 }}>through</span>
                <br />
                {flightDateEnd}
            </>
        )
    }

    protected buildTestStrategyValue(): string | undefined {
        if (!this.test) return undefined

        const spec = this.sample.delivery_spec
        const window = spec?.window
        const type = spec?.type

        if (window === NotificationDeliveryWindow.HOLD_OUT) {
            return titleCase('hold out')
        } else if (window === NotificationDeliveryWindow.INFORMED) {
            return titleCase('informed')
        } else if (type === NotificationDeliveryType.IMMEDIATE) {
            return titleCase('immediate')
        } else if (window === NotificationDeliveryWindow.STANDARD) {
            return titleCase('fixed')
        } else if (window === NotificationDeliveryWindow.TIMEZONE) {
            return titleCase('subscriber time zone')
        }

        return undefined
    }

    protected renderTestChannelValues(): React.ReactNode {
        const notifications = this.test?.notifications
        if (!notifications) return undefined

        const hasWeb = notifications.find((n) => n.channels?.includes(DeliveryChannel.WEB))
        const hasIos = notifications.find((n) => n.channels?.includes(DeliveryChannel.NATIVE_IOS))
        const hasAndroid = notifications.find((n) => n.channels?.includes(DeliveryChannel.NATIVE_ANDROID))

        const items: React.ReactNode[] = []
        if (hasWeb)
            items.push(
                <span key="web">
                    <ChromeFilled /> Web
                </span>,
            )
        if (hasIos)
            items.push(
                <span key="ios">
                    <AppleFilled /> Native Apple
                </span>,
            )
        if (hasAndroid)
            items.push(
                <span key="android">
                    <AndroidFilled /> Native Android
                </span>,
            )

        if (items.length === 0) return undefined

        return items.reduce<React.ReactNode[]>((acc, item, i) => {
            if (i > 0) acc.push(', ')
            acc.push(item)
            return acc
        }, [])
    }

    protected renderTestBehaviorsField(): React.ReactNode {
        const fields: React.ReactNode[] = []

        if (this.test?.notifications?.find((n) => !!n.template?.channels?.default?.is_silent)) {
            fields.push(
                <DetailsSummaryField
                    key="silent-delivery"
                    label="Silent Delivery"
                    value="Enabled"
                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.silentDelivery, inline: true }}
                />,
            )
        }

        if (fields.length === 0) return null

        return <>{fields}</>
    }

    protected renderTotalVariantsField(): React.ReactNode {
        const notifications = this.test?.notifications
        if (!notifications) return null

        const variantCount = notifications.filter((n) => n.ab_test?.role !== AbTestNotificationRole.ROLLOUT).length
        const hasWinner = notifications.some((n) => n.ab_test?.role === AbTestNotificationRole.ROLLOUT)

        const value = `${variantCount} Test Variant${variantCount !== 1 ? 's' : ''}${
            hasWinner ? ' and 1 Winner Notification' : ''
        }`

        return (
            <DetailsSummaryField
                label="Total Variants"
                value={value}
                info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.totalVariants, inline: true }}
            />
        )
    }

    protected renderTestContentSection(): React.ReactNode {
        const notifications = this.test?.notifications
        if (!notifications) return null

        let pushlyAiCount = 0
        let pageAnalyzerCount = 0
        let manualCount = 0

        for (const n of notifications) {
            const aiResults = n.template?.channels?.default?.auto_suggested_fields_results as
                | Record<string, unknown>
                | null
                | undefined

            if (!aiResults) {
                manualCount++
                continue
            }

            let aiSource = aiResults.source as string | undefined
            if (
                !aiSource &&
                (aiResults.title ||
                    aiResults.body ||
                    aiResults.imageUrl ||
                    (Array.isArray(aiResults.keywords) && aiResults.keywords.length > 0))
            ) {
                aiSource = 'page_analyzer'
            }

            if (aiSource === 'pushly_ai') pushlyAiCount++
            else if (aiSource === 'page_analyzer') pageAnalyzerCount++
            else manualCount++
        }

        const parts: React.ReactNode[] = []
        if (pushlyAiCount > 0)
            parts.push(
                <span key="ai">
                    <PushlyAi /> Pushly AI ({pushlyAiCount})
                </span>,
            )
        if (pageAnalyzerCount > 0) parts.push(<span key="pa">Page Analyzer ({pageAnalyzerCount})</span>)
        if (manualCount > 0) parts.push(<span key="manual">Manual ({manualCount})</span>)

        if (parts.length === 0) return null

        const value = parts.reduce<React.ReactNode[]>((acc, part, i) => {
            if (i > 0)
                acc.push(
                    <span key={`sep-${i}`} style={{ opacity: 0.5 }}>
                        {' '}
                        •{' '}
                    </span>,
                )
            acc.push(part)
            return acc
        }, [])

        return (
            <DetailsSummarySection title="Content">
                <DetailsSummaryField
                    label="Content Sources"
                    value={<>{value}</>}
                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.contentSources, inline: true }}
                />
            </DetailsSummarySection>
        )
    }

    protected renderSegmentFields(): React.ReactNode {
        let subscriberTags: React.ReactNode
        let excludedSubscribers: React.ReactNode
        const allSubscriberTag: React.ReactNode = (
            <a key="all-subs">
                <Tag variant="outlined" color="primary" className="no-click">
                    All Subscribers
                </Tag>
            </a>
        )

        if (this.sample.audience.segments) {
            let tags: React.ReactNode[] = [allSubscriberTag]
            const isAllSubscribers = this.sample.audience.segments.find((s) => s.is_default)

            if (!isAllSubscribers) {
                tags = this.sample.audience.segments
                    .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
                    .map((segment, idx: number) => (
                        <a
                            key={`i-${idx}`}
                            onClick={this.jumpToSegment.bind(this, segment)}
                            href={this.buildSegmentUrl(segment)}
                        >
                            <Tag variant="outlined">{segment.name}</Tag>
                        </a>
                    ))
            }

            subscriberTags = <>{tags}</>
        } else if (this.sample.audience.external_subscriber_ids) {
            subscriberTags = <Tag variant="outlined">Subscriber IDs</Tag>
        }

        if (this.sample.audience.excluded_segments && this.sample.audience.excluded_segments.length > 0) {
            excludedSubscribers = (
                <>
                    {this.sample.audience.excluded_segments
                        .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0))
                        .map((segment, idx: number) => (
                            <a
                                key={`x-${idx}`}
                                onClick={this.jumpToSegment.bind(this, segment)}
                                href={this.buildSegmentUrl(segment)}
                            >
                                <Tag variant="outlined">{segment.name}</Tag>
                            </a>
                        ))}
                </>
            )
        }

        return (
            <>
                <DetailsSummaryField
                    label="Targeted Audience(s)"
                    value={subscriberTags}
                    info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.targetedAudiences, inline: true }}
                />
                {!!excludedSubscribers && (
                    <DetailsSummaryField
                        label="Excluded Audience(s)"
                        value={excludedSubscribers}
                        info={{ title: NOTIFICATION_FIELD_DESCRIPTIONS.excludedAudiences, inline: true }}
                    />
                )}
            </>
        )
    }

    private buildSegmentUrl(segment: GetSegmentResponse): string {
        return this.appService.routeWithinDomain(`/segments/${segment.id}/summary`, true)
    }

    @autobind
    private jumpToSegment(segment: GetSegmentResponse, ev?: React.MouseEvent): void {
        if (!ev?.metaKey) {
            preventBubbling(ev)
            this.appService.route(this.buildSegmentUrl(segment))
        }
    }
}

interface IProps {}

interface IState extends IBaseNotificationDetailsState {
    domain: DomainDto
    preloading: boolean
    statsType: 'hour' | 'day'
    loadingGenStats: boolean
    loadingBreakdownStats: boolean
    test?: GetDomainNotificationABTestResponse
    isDelivered: boolean
    testStats: {
        composite: any
        compositeDaily: any
        schedules: any[]
        rawStats: any[]
    }
    treatments: INotificationTreatment[]
    winningTreatmentIndex: number
    performanceChannel: DeliveryChannel[]
    selectedPreviewPlatforms: Record<string, PreviewPlatform>
}

@observer
export class TestDetails extends BaseNotificationDetails<IProps, IState> {
    protected appService: AppService
    protected readonly notificationPreviewState: NotificationPreviewState
    private appState: AppState
    private notificationService: NotificationService
    private insightsService: InsightsService

    private disposeObservers: (() => void)[]

    private get testParamId(): number | undefined {
        try {
            return parseInt(this.injectedProps.match.params.testId, 10)
        } catch {
            return
        }
    }

    public constructor(props: IProps & FormComponentProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
        this.notificationService = Container.get(NotificationService)
        this.insightsService = Container.get(InsightsService)
        this.notificationPreviewState = Container.get(NotificationPreviewState)

        this.state = {
            domain: this.globalDomain!,
            preloading: true,
            statsType: 'day',
            loadingGenStats: true,
            loadingBreakdownStats: true,
            isDelivered: false,
            treatments: [],
            winningTreatmentIndex: 0,
            testStats: {
                composite: {},
                compositeDaily: {},
                schedules: [],
                rawStats: [],
            },
            performanceChannel: [],
            selectedPreviewPlatforms: {},
        } as any
    }

    public async UNSAFE_componentWillMount() {
        await this.preloadRequiredDataStates()

        const tabs = [
            {
                name: 'summary',
                label: 'Performance',
                render: this.renderSummaryTab,
            },
            {
                name: 'variants',
                label: 'Notifications',
                render: this.renderVariantsTab,
            },
        ]

        const activeTab = this.determineActiveTab(tabs)

        this.setState({
            tabs,
            activeTab,
        })
    }

    public componentDidMount(): void {
        this.disposeObservers = [observe(this.appState, 'currentDomainJsonData', () => this.setDomainState())]

        this.setDomainState()
    }

    public componentWillUnmount() {
        super.componentWillUnmount()
        this.disposeObservers.forEach((fn) => fn())
    }

    public render() {
        return (
            <div className="module test-details">
                {!this.state.preloading && (
                    <React.Fragment>
                        <TestDetailsHeader test={this.state.test} domain={this.localDomain!} />

                        {this.renderTabs()}
                    </React.Fragment>
                )}
            </div>
        )
    }

    /**
     * Returns true when the test has reached a state where performance
     * stats are expected to exist (delivered, delivering TZ push, or
     * cancelled TZ push).  Pre-delivery statuses (SCHEDULED, DRAFT,
     * QUEUED, etc.) return false.
     */
    private get hasPerformanceData(): boolean {
        const status = this.state.test!.schedules[0].status
        const completedStatuses = [StatusType.COMPLETED.name, StatusType.COMPLETED_WITH_FAILURES.name]
        const isDelivered = arrayContains(completedStatuses, status)
        const isDelivering = status === StatusType.DELIVERING.name
        const isCancelled = status === StatusType.CANCELLED.name || status === StatusType.CANCELLING.name
        const isTzPush = this.state.test!.schedules[0].delivery_spec?.window === NotificationDeliveryWindow.TIMEZONE

        return isDelivered || (isTzPush && (isDelivering || isCancelled))
    }

    private renderSummaryTab(): React.ReactNode {
        if (!this.hasPerformanceData) {
            const sample = this.state.test!.schedules[0]
            const isSTZ = isTzDelivery(sample)
            const sendDateFormat = isSTZ ? `${BASE_TIME_FORMAT_WITHOUT_TZ} [STZ]` : BASE_TIME_FORMAT
            const sendDate = moment.tz(sample.delivery_spec.send_date_utc, sample.delivery_spec.timezone)

            return (
                <Well className="thin gen-stats-well nested" variant="outline" hideHeader={true} hideFooter={true}>
                    <div className="performance-empty-state">
                        <ClockCircleOutlined className="empty-state-icon" aria-hidden="true" />
                        <span className="empty-state-title">
                            Performance data will be available once delivery begins.
                        </span>
                        <span className="empty-state-subtitle">Scheduled for {sendDate.format(sendDateFormat)}</span>
                    </div>
                </Well>
            )
        }

        return (
            <React.Fragment>
                <div className="outer-well-buttons">
                    <Tooltip title="Refresh">
                        <Button
                            className="refresh-list"
                            type="text"
                            shape="round"
                            icon={
                                <ReloadOutlined
                                    className="text--size--large"
                                    spin={this.state.loadingGenStats || this.state.loadingBreakdownStats}
                                />
                            }
                            onClick={this.refreshStats}
                        />
                    </Tooltip>
                </div>

                {this.renderGenericStats()}
                <br />

                {this.renderGenPerformanceChart()}
            </React.Fragment>
        )
    }

    private renderVariantsTab(): React.ReactNode {
        const { treatments } = this.state
        const sortedTreatments = this.sortById(treatments)

        return (
            <div className="test-result-cards">
                {sortedTreatments.map((treatment, index: number) => {
                    const name: string = ['A', 'B', 'C', 'D', 'E'][index]
                    return this.buildTestResult(`Variant ${name}`, treatment, index)
                })}
            </div>
        )
    }

    private renderGenPerformanceChart(): React.ReactNode {
        const { testStats } = this.state

        if (!testStats) return ''

        // Safely handle cases where rawStats may be undefined during initial render
        const rawStats = Array.isArray(testStats.rawStats) ? testStats.rawStats : []

        const channelOptions = Array.from(new Set(rawStats.map((stat) => stat?.channel).filter(Boolean)))
        const channelsSelection: any = {}
        channelOptions.forEach((ch) => {
            channelsSelection[ch.toUpperCase()] = this.state.performanceChannel.includes(ch)
        })
        const mapDataKeyFromObject = (key: string, obj: any[], channel: DeliveryChannel) => {
            return (obj ?? [])?.filter((val) => val.channel === channel)?.map((filtered) => filtered[key])
        }

        const buildSeries = (chartType: 'impressions' | 'clicks') => {
            const series: any[] = []

            if (this.state.performanceChannel.includes(DeliveryChannel.WEB)) {
                series.push({
                    name: `Web`,
                    color: INSIGHTS_WEB_COLOR,
                    data: mapDataKeyFromObject(chartType, rawStats, DeliveryChannel.WEB),
                })
            }

            if (this.state.performanceChannel.includes(DeliveryChannel.NATIVE_IOS)) {
                series.push({
                    name: `iOS`,
                    chartType,
                    color: INSIGHTS_IOS_COLOR,
                    data: mapDataKeyFromObject(chartType, rawStats, DeliveryChannel.NATIVE_IOS),
                })
            }

            if (this.state.performanceChannel.includes(DeliveryChannel.NATIVE_ANDROID)) {
                series.push({
                    name: `Android`,
                    chartType,
                    color: INSIGHTS_ANDROID_COLOR,
                    data: mapDataKeyFromObject(chartType, rawStats, DeliveryChannel.NATIVE_ANDROID),
                })
            }

            return series
        }

        const options = (chartType: 'impressions' | 'clicks') => ({
            ...DEFAULT_HIGHCHARTS_CONFIG,
            chart: {
                type: 'column',
            },
            title: {
                text: undefined,
            },
            yAxis: [
                {
                    type: 'logarithmic',
                    title: { text: '' },
                    startOnTick: 0,
                    minRange: 0,
                },
            ],
            xAxis: {
                categories: [titleCase(chartType)],
            },
            legend: {
                enabled: false,
            },
            plotOptions: {
                column: {
                    stacking: false,
                },
            },
            tooltip: {
                pointFormatter: HighchartsUtils.commaTooltipPointFormatter,
                formatter: HighchartsUtils.defaultTooltipFormatter,
                shared: true,
            },
            series: buildSeries(chartType),
        })

        return (
            <Well
                className="thin"
                hideHeader={true}
                hideFooter={true}
                loading={this.state.loadingBreakdownStats}
                disabled={!this.state.test!.notifications}
            >
                <div className="chart-header">
                    <div className="chart-title">Performance Summary by Channel</div>
                    <div className="chart-actions">
                        <DeliveryChannelSelector
                            type="multiple"
                            nested={true}
                            onChange={(change: DeliveryChannel[]) => {
                                this.setState({ performanceChannel: change })
                            }}
                            visibleChannels={channelOptions}
                            allowEmptySelection={true}
                        />
                    </div>
                </div>

                <div className="chart-body">
                    <HighchartsReact highcharts={Highcharts} options={options('impressions')} />

                    <HighchartsReact highcharts={Highcharts} options={options('clicks')} />
                </div>
            </Well>
        )
    }

    private renderGenericStats(): React.ReactNode {
        const stats = this.state.testStats.composite
        const test = this.state.test

        // Determine status
        let statusStr = StatusType.SCHEDULED.name
        if (test) {
            const hasStarted = test.schedules.some(
                (s) => !!s.started_date_utc || s.delivery_spec.type === NotificationDeliveryType.IMMEDIATE,
            )
            const hasFullyCompleted = !test.schedules.some((s) => !s.completed_date_utc)
            const hasFullyFailed = !test.schedules.some((s) => s.status !== StatusType.FAILED.name)
            const isPartiallyDelivered = test.schedules.some(
                (s) => s.status === StatusType.COMPLETED_WITH_FAILURES.name,
            )

            if (hasFullyFailed) {
                statusStr = StatusType.FAILED.name
            } else if (hasFullyCompleted) {
                statusStr = StatusType.COMPLETED.name
            } else if (hasStarted) {
                statusStr = StatusType.DELIVERING.name
            }
        }

        const items: StatItem[] = [
            {
                key: 'status',
                type: 'custom',
                label: 'Status',
                neverDisabled: true,
                render: () => (
                    <Space size={4}>
                        <StatusBadge type="notification" status={statusStr} expanded={true} />
                        {test?.schedules.some((s) => s.status === StatusType.COMPLETED_WITH_FAILURES.name) && (
                            <Tooltip title="A portion of this notification's audience failed to deliver.">
                                <ExclamationCircleOutlined className="info-icon error-icon" />
                            </Tooltip>
                        )}
                    </Space>
                ),
            },
            {
                key: 'impressions',
                type: 'numeric',
                label: 'Impressions',
                value: stats?.impressions ?? 0,
                animated: true,
            },
            {
                key: 'clicks',
                type: 'numeric',
                label: 'Total Clicks (CTR)',
                value: stats?.clicks ?? 0,
                subValue: stats?.clicks && stats?.impressions ? stats.clicks / stats.impressions : undefined,
                subValueFormat: 'percent',
                animated: true,
            },
        ]

        return <StatsWell loading={this.state.loadingGenStats} items={items} />
    }

    private async preloadRequiredDataStates(): Promise<void> {
        const state: Partial<IState> = {}

        if (this.globalDomain && this.testParamId) {
            const test = await this.notificationService.fetchTestById(this.globalDomain.id, this.testParamId, true)

            if (test) {
                state.preloading = false
                state.test = test
                state.treatments = test.notifications.map(this.extractNotificationTreatment)

                const sample: GetNotificationScheduleResponse = test.schedules[0]
                if (sample && sample.status === 'COMPLETED') {
                    state.isDelivered = true
                }

                await this.setState(state as any)

                // Only fetch stats when the test has reached a delivery state
                if (this.hasPerformanceData) {
                    this.getNotificationGenStats(test.notifications).catch((err) => console.warn(err))
                    this.getNotificationBreakdownStats(test.schedules).catch((err) => console.warn(err))
                } else {
                    this.setState({ loadingGenStats: false, loadingBreakdownStats: false })
                }
            }
        }
    }

    private reduceStat(key: string, stats: any[]): any[] {
        return stats.reduce((n: number, stat: any) => (n += stat[key]), 0)
    }

    private async getNotificationGenStats(notifications?: GetNotificationResponse[]): Promise<void> {
        notifications = notifications! || this.state.test!.notifications
        const notificationIds = notifications.map((n) => n.id)

        await this.setState({ loadingGenStats: true })

        const stats = await this.insightsService.fetch(
            {
                entity: ReportEntity.NOTIFICATIONS,
                date_preset: ReportDatePreset.LIFETIME,
                date_increment: ReportDateIncrement.LIFETIME,
                action_attribution: 'send_time',
                breakdowns: ['channel', 'notification'],
                fields: ['notification.id', 'deliveries', 'impressions', 'clicks', 'channel'],
                filters: [
                    {
                        field: 'domain.id',
                        operator: 'eq',
                        value: this.appState.currentDomain!.id,
                    },
                    {
                        field: 'notification.id',
                        operator: 'in',
                        value: notificationIds,
                    },
                    {
                        field: 'channel',
                        operator: 'in',
                        value: DeliveryChannel.getAllChannels(),
                    },
                ],
            },
            {
                cancellationKey: 'ntd.getNotifGenStats',
            },
        )

        let compositeStats

        if (stats) {
            compositeStats = {
                clicks: this.reduceStat('clicks', stats),
                deliveries: this.reduceStat('deliveries', stats),
                impressions: this.reduceStat('impressions', stats),
            }
        }

        this.setState(({ testStats }) => ({
            loadingGenStats: false,
            testStats: {
                ...testStats,
                composite: compositeStats ?? {},
                schedules: stats,
            },
        }))
    }

    @autobind
    private async refreshStats(): Promise<void> {
        this.preloadRequiredDataStates()
        this.getNotificationGenStats(this.state.test!.notifications)
        this.getNotificationBreakdownStats(this.state.test!.schedules)
    }

    private async getNotificationBreakdownStats(schedules?: GetNotificationScheduleResponse[]): Promise<void> {
        const { test } = this.state
        const data: GetNotificationScheduleResponse[] = schedules || test!.schedules
        const notifIds = data.map((s) => s.notification_id)
        const compositeBreakdownStats = {}

        await this.setState({ loadingBreakdownStats: true })

        const rawStats = await this.insightsService.fetch({
            entity: ReportEntity.NOTIFICATIONS,
            action_attribution: 'event_time',
            date_increment: ReportDateIncrement.LIFETIME, // statsType,
            date_preset: ReportDatePreset.LIFETIME,
            breakdowns: ['domain', 'channel'],
            fields: ['deliveries', 'impressions', 'clicks', 'channel'],
            filters: [
                { field: 'domain.id', operator: 'eq', value: this.state.domain.id },
                { field: 'notification.id', operator: 'in', value: notifIds },
                { field: 'channel', operator: 'in', value: DeliveryChannel.getAllChannels() },
            ],
        })

        this.setState({ performanceChannel: Array.from(new Set(rawStats?.map((stat) => stat?.channel) ?? [])) })
        this.setState(({ testStats }) => ({
            loadingBreakdownStats: false,
            testStats: {
                ...testStats,
                compositeDaily: compositeBreakdownStats,
                rawStats,
            },
        }))
    }

    private async setDomainState(): Promise<void> {
        const globalDomainIsSet = !!this.globalDomain
        const localDomainIsUnset = globalDomainIsSet && !this.localDomain
        const localDomainIsMismatch =
            globalDomainIsSet && !!this.localDomain && this.localDomain.id !== this.globalDomain!.id

        if (localDomainIsMismatch) {
            this.appService.routeWithinDomain('/notifications')
        }

        if (localDomainIsUnset) {
            await this.setState({ domain: this.globalDomain! })
        }
    }

    private sortById<T>(arr: T[]): T[] {
        const result = clone(arr)
        result.sort((a: any, b: any) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0))

        return result
    }

    private buildTestResult(name: string, _treatment: any, index: number): JSX.Element {
        const { testStats } = this.state

        const notifications = Array.isArray(this.state.test?.notifications) ? this.state.test!.notifications : []
        const notification: any = this.sortById(notifications)[index]

        // Guard schedules and match by notificationId (not by index)
        const schedules = Array.isArray(this.state.test?.schedules) ? this.state.test!.schedules : []
        const schedule: any = schedules.find(
            (s: any) => s?.notification_id?.toString() === notification?.id?.toString(),
        )

        // Guard stats.schedules before find
        const statsSchedules = Array.isArray(testStats?.schedules) ? testStats.schedules : []
        const stats = statsSchedules.find(
            (s) => (s?.notification || {}).id?.toString() === notification?.id?.toString(),
        )

        const isWinner = notification?.ab_test?.winner || false
        const isResult = !!notification?.ab_test?.winnerOriginId
        const isPromotedWinner = notification?.ab_test?.role === AbTestNotificationRole.ROLLOUT

        const backgroundColor = {
            'Variant A': 'test-a-color',
            'Variant B': 'test-b-color',
            'Variant C': 'test-c-color',
            'Variant D': 'test-d-color',
            'Variant E': 'test-e-color',
        }[name]

        const buildScheduleDetailsUrl = (notificationId: string): string => {
            if (!notificationId) {
                return this.appService.routeWithin('domain', `/notifications`, true)
            }

            return this.appService.routeWithin('domain', `/notifications/${notificationId}`, true)
        }

        const handleSummaryTitleClick = (ev: React.MouseEvent<HTMLAnchorElement>, notificationId: string) => {
            this.stopPropagation(ev.nativeEvent)

            if (ev.metaKey) {
                window.open(buildScheduleDetailsUrl(notificationId))
                return
            }

            this.appService.route(buildScheduleDetailsUrl(notificationId))
        }

        const channelOptions = (notification?.channels ??
            notification?.template?.channels?.default?.channels) as DeliveryChannel[]
        const npsCache = this.notificationPreviewState.getCache('domain', this.state.domain.id)
        const initialPlatform = npsCache.resolvePlatformFromAvailableChannels(channelOptions || [])
        const previewState = npsCache.state

        const platform = notification
            ? this.state.selectedPreviewPlatforms[notification.id] ?? initialPlatform
            : initialPlatform

        const { label: replacementBehaviorLabel, replacementGroupKey } = getCollapseKeyDisplayLabel(
            notification?.collapse_key,
            this.state.domain?.collapseKeyType,
            { domainId: notification?.domain_id, notificationId: notification?.id },
        )
        const replacementBehaviorDisplay = replacementGroupKey
            ? `${replacementBehaviorLabel}: ${replacementGroupKey}`
            : replacementBehaviorLabel

        return (
            <NotificationSummaryCard
                key={index}
                mode="dark"
                className={backgroundColor}
                loading={this.state.loadingGenStats}
                title={
                    isPromotedWinner ? (
                        'Promoted Winner'
                    ) : isWinner ? (
                        <>
                            <span>{name}</span>
                            <NotificationAbWinnerBadge />
                        </>
                    ) : isResult ? (
                        'Winning Notification'
                    ) : (
                        name
                    )
                }
                titleUrl={buildScheduleDetailsUrl(notification?.id)}
                onTitleClick={(ev) => handleSummaryTitleClick(ev, notification?.id)}
                showWeight={true}
                domain={this.state.domain}
                notification={{
                    ...extractSummaryNotification(notification, platform),
                    abTest: notification?.ab_test,
                    statusId: schedule ? StatusType[schedule.status].id : undefined,
                    schedule: schedule ? {
                        type: schedule.delivery_spec?.type,
                        window: schedule.delivery_spec?.window,
                        sendDateUtc: schedule.delivery_spec?.send_date_utc,
                        timezone: schedule.delivery_spec?.timezone,
                        deliverySpec: schedule.delivery_spec ? {
                            type: schedule.delivery_spec.type,
                            window: schedule.delivery_spec.window,
                            sendDateUtc: schedule.delivery_spec.send_date_utc,
                            timezone: schedule.delivery_spec.timezone,
                            ttlSeconds: schedule.delivery_spec.ttl_seconds,
                            ttlMetric: schedule.delivery_spec.ttl_metric,
                        } : undefined,
                        startedDateUtc: schedule.started_date_utc,
                        completedDateUtc: schedule.completed_date_utc,
                    } : undefined,
                    replacementBehavior: this.state.domain?.flags?.includes(FEAT_ENABLE_NOTIFICATION_DISPLAY_BEHAVIOR)
                        ? replacementBehaviorDisplay
                        : undefined,
                    aiFields: extractAiFields(
                        notification?.template?.channels?.default?.autoSuggestedFieldsResults,
                    ),
                }}
                channels={channelOptions || []}
                initialPlatform={platform}
                onPlatformChange={(nextPlatform: PreviewPlatform) => {
                    this.setState(({ selectedPreviewPlatforms, ...state }) => {
                        const updatedPreviewPlatforms = structuredClone(selectedPreviewPlatforms)
                        if (notification?.id) {
                            updatedPreviewPlatforms[notification.id] = nextPlatform
                        }

                        return {
                            ...state,
                            selectedPreviewPlatforms: updatedPreviewPlatforms,
                        }
                    })
                }}
                previewState={previewState}
                showScheduleDetails={!!this.state.test?.is_time_variant}
                stats={stats}
                hideFooter={true}
                userId={this.appState.currentUser?.id}
            />
        )
    }

    private extractNotificationTreatment(notification: GetNotificationResponse): INotificationTreatment {
        const defaultChannel = notification.template?.channels?.default
        return {
            id: (notification.id as any) || randomstring.generate(5),
            title: defaultChannel?.title || undefined,
            body: defaultChannel?.body || undefined,
            image: defaultChannel?.image_url || undefined,
            icon: defaultChannel?.icon_url || undefined,
            badge: defaultChannel?.badge_url || undefined,
            landingUrl: defaultChannel?.landing_url || undefined,
            landingUrlDisabled: !defaultChannel?.landing_url,
            isSilent: defaultChannel?.is_silent,
            requireInteraction: defaultChannel?.require_interaction,
            keywords: notification.template?.keywords ?? [],
        } as any
    }

    private stopPropagation(ev: Event): void {
        ev.preventDefault()
        ev.stopPropagation()
    }
}
